import { useQuery, useMutation } from '@apollo/client/react';
import { Grid, FormGroup, FormControlLabel, Switch, Typography } from '@mui/material';
import { useContext } from 'react';

import RequestPermissionTooltip from '@/components/RequestPermissionTooltip';
import PermissionContext from '@/Contexts/Permissions';
import {
  GetContractTypesDocument,
  GetCompanyContractTypesDocument,
  DestroyCompanyContractTypeDocument,
  CreateCompanyContractTypeDocument,
  type Contract,
  PermissionActionEnum,
  PermissionEntityEnum,
} from '@/graphql/generated/graphql';
import { displayNotification } from '@/utils/notifications';

const CompanyContractTypesSection: React.FC = () => {
  const { canPerformAction, checkRequestExists, requestPermission } = useContext(PermissionContext);
  const { Manage } = PermissionActionEnum;
  const { CompanySettings } = PermissionEntityEnum;
  const hasPermission = canPerformAction(Manage, CompanySettings);

  const { data: contractData } = useQuery(GetContractTypesDocument);
  const { data: companyContractData } = useQuery(GetCompanyContractTypesDocument);
  const [destroyCompanyContractTypes] = useMutation(DestroyCompanyContractTypeDocument, {
    refetchQueries: ['getCompanyContractTypes'],
    onCompleted: () => displayNotification({ type: 'success', text: 'Contract type removed successfully!' }),
  });
  const [createCompanyContractType] = useMutation(CreateCompanyContractTypeDocument, {
    refetchQueries: ['getCompanyContractTypes'],
    onCompleted: () => displayNotification({ type: 'success', text: 'Contract type added successfully!' }),
  });

  const isContractEnabled = (contractId: string) => {
    return companyContractData?.getCompanyContractTypes.some((c) => c.contractTypeId === contractId);
  };

  const handleContractToggle = (contractId: string, checked: boolean) => {
    if (checked) {
      createCompanyContractType({
        variables: { contractType: { contractTypeId: contractId } },
      });
    } else {
      destroyCompanyContractTypes({ variables: { id: contractId } });
    }
  };

  return (
    <Grid size={12}>
      <FormGroup>
        {contractData?.getContractTypes?.map((contract: Contract) => (
          <FormControlLabel
            key={contract.name}
            control={
              <RequestPermissionTooltip
                hasPermission={hasPermission}
                requiredPermission={{ action: Manage, entity: CompanySettings }}
                checkRequestExists={checkRequestExists}
                onRequest={requestPermission}
              >
                <Switch
                  checked={isContractEnabled(contract.id)}
                  onChange={(_, checked) => handleContractToggle(contract.id, checked)}
                  color="primary"
                  disabled={!hasPermission}
                />
              </RequestPermissionTooltip>
            }
            label={
              <Grid container direction="column">
                <Typography variant="body1">{contract.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {contract.description}
                </Typography>
              </Grid>
            }
            sx={{ mb: 2 }}
          />
        ))}
      </FormGroup>
    </Grid>
  );
};

export default CompanyContractTypesSection;
